import React, { useCallback, useEffect, useState, useReducer } from "react";
import "./PropertieMPIGaugeChartComponent";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../../../../Components";
import { GetMpiGaugeChartDataAPI } from "../../../../../../Services";
import { GaugeChart } from "./GaugeChart";
import { PercentageDifferenceMarketPrice } from "../../../../../../Helper"; 

export const PropertieMPIGaugeChartComponent = ({
  unitId,
  marketComparison,
  activeData,
  operationType,
  parentTranslationPath,
  translationPath = "",
}) => {
  const { t } = useTranslation(parentTranslationPath, "Shared");
  const [isLoading, setIsLoading] = useState(false);
  const reducer = (select, action) => {
    if (action.id !== "edit") return { ...select, [action.id]: action.value };
    if (action.id === "edit") {
      return {
        ...action.value,
      };
    }
  };
  const [defaultState, setDefaultState] = useState({
    minPrice: null,
    maxPrice: null,
    medianPrice: null,
    medianPricePerSqft: null,
    psiPrice: null,
    noMPIDataFoundForTheUnit: null,
    isShowChat  :false , 
  });
  const [
    percentageDifferenceFromMarketPrice,
    setPercentageDifferenceFromMarketPrice,
  ] = useState(null);
  const [state, setState] = useReducer(reducer, {
    ...defaultState,
  });
  const [PSIPriceWithinRange, setPSIPriceWithinRange] = useState(null);

  const RangeofMPIGauge = useCallback(
    (psiPrice, medianPricePerSqft, isLessThanThreeActiveMarketListings) => {
      let areaSizeSQFt = activeData?.builtup_area_sqft
        ? Number(activeData?.builtup_area_sqft)
        : 0;
      const calculatedMedianPrice =
        areaSizeSQFt && medianPricePerSqft
          ? Number(medianPricePerSqft * areaSizeSQFt)
          : 0;
      const minimumBounded = calculatedMedianPrice * 0.5;
      const maximumBounded = calculatedMedianPrice * 1.5;
      if (!isLessThanThreeActiveMarketListings) {
        return {
          key: "MarketListingsAreLessThanThree",
          value: "market-listings-are-less-than-three",
        };
      } else if (!psiPrice && !calculatedMedianPrice)
        return {
          key: "Insufficient data",
          value: "insufficient-data",
        };
      else if (!psiPrice)
        return {
          key: "unit-price-missing",
          value: "unit-price-missing",
        };
      else if (!calculatedMedianPrice)
        return {
          key: "market-average-unavailable",
          value: "market-average-unavailable",
        };
      else if (psiPrice > maximumBounded)
        return {
          key: "isMaximumBounded",
          value: "the-unit-price-exceeds-the-market-average-by-more-than",
        };
      else if (psiPrice < minimumBounded)
        return {
          key: "minimumBounded",
          value: "the-unit-price-is-over-lower-than-the-market-average",
        };
      return {
        key: "PSIPriceWithinRange",
        value: "PSI Price Within Range",
      };
    },
    [unitId, activeData]
  );

  const GetUnitMpiGaugeChartInfo = useCallback(async () => {
    setIsLoading(true);
    const rs = await GetMpiGaugeChartDataAPI({
      unitId: unitId,
      operationType: operationType,
    });
    if (!(rs && rs.status && rs.status !== 200)) {
      setPSIPriceWithinRange(
        RangeofMPIGauge(rs.psiPrice, rs.medianPricePerSqft , rs?.isShowChat)
      );
      setPercentageDifferenceFromMarketPrice(
        rs?.isShowChat
          ? PercentageDifferenceMarketPrice(
              rs?.psiPrice || 0,
              rs?.medianPricePerSqft || 0,
              activeData?.builtup_area_sqft || 0
            )
          : null
      );
      if (rs)
        setState({
          id: "edit",
          value: { ...rs, noMPIDataFoundForTheUnit: null },
        });
    } else if (rs && rs.status && rs.status === 400) {
      setState({
        id: "edit",
        value: {
          ...defaultState,
          noMPIDataFoundForTheUnit: rs?.data,
        },
      });
      setPSIPriceWithinRange(null);
      setPercentageDifferenceFromMarketPrice(null);
    } else {
      setState({ id: "edit", value: defaultState });
      setPSIPriceWithinRange(null);
      setPercentageDifferenceFromMarketPrice(null);
    }
    setIsLoading(false);
  });

  useEffect(() => {
    if (unitId !== undefined && operationType !== undefined) {
      GetUnitMpiGaugeChartInfo();
    }
  }, [unitId, operationType, activeData]);

  return (
    <div className="w-100 mb-3">
      <Spinner isActive={isLoading} isAbsolute />

      {!isLoading &&
      PSIPriceWithinRange &&
      PSIPriceWithinRange.key === "PSIPriceWithinRange" ? (
        <div className="d-flex GaugeChart">
          <GaugeChart
            activeData={activeData}
            minPrice={state?.minPrice || 0}
            maxPrice={state?.maxPrice}
            medianPricePerSqft={state?.medianPricePerSqft || 0}
            medianPrice={
              state?.medianPricePerSqft * activeData.builtup_area_sqft || 0
            }
            marketComparison={marketComparison}
            psiPrice={state?.psiPrice || 0}
            translationPath={translationPath}
            parentTranslationPath={parentTranslationPath}
            percentageDifferenceFromMarketPrice={
              percentageDifferenceFromMarketPrice
            }
          />
        </div>
      ) : (
        PSIPriceWithinRange &&
        PSIPriceWithinRange.key &&
        PSIPriceWithinRange.key !== "PSIPriceWithinRange" &&
        !isLoading && (
          <div className="px-2 fz-16px">
            <h6 className="px-2 fz-16px fw-bold">
              {t(`${translationPath}mpi`)}
            </h6>
            <h5 className="c-primary " style={{ "line-height": "22px" }}>
              {t(`${translationPath}${PSIPriceWithinRange?.value}`)}
            </h5>
          </div>
        )
      )}
      {isLoading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "150px" }}
        >
          <div className="spinner-border text-primary" role="status"></div>
          <div className="px-3">
            <h5 className="d-block fz-18px fw-bold">
              {t(`${translationPath}mpi`)}
            </h5>
            <h5 className="fw-bold c-primary">
              {t(`${translationPath}please-wait-data-is-loading`)}
            </h5>
          </div>
        </div>
      )}
      {!isLoading && !PSIPriceWithinRange && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "150px" }}
        >
          <div className="spinner-border text-primary" role="status"></div>
          <div className="px-3">
            <h5 className="d-block fz-18px fw-bold">
              {t(`${translationPath}mpi`)}
            </h5>
            <h5 className="fw-bold c-primary">
              {t(`${translationPath}no-mpi-data-found-for-the-unit`)}
            </h5>
          </div>
        </div>
      )}
    </div>
  );
};
